import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TutorTokenBreakdown } from '../../../types/tutorAi';
import { formatCount, formatPercent } from '../../../utils/format';
import { buildTokenComposition } from '../../../utils/tokenComposition';
import { CompositionBar } from '../../atoms/CompositionBar';
import { SurfaceCard } from '../../molecules/SurfaceCard';

export interface TokenCompositionCardProps {
  readonly breakdown: readonly TutorTokenBreakdown[];
}

/**
 * Composição do consumo de tokens por provedor e operação.
 *
 * Não fixa baldes: desenha as séries que a API devolver, para que a troca de modelo ou a
 * entrada de uma operação nova não exija mexer na tela. O que é fixo é o **número de
 * cores** — acima do teto da paleta a cauda vira uma entrada "Outros", porque o
 * `@ads/tokens` não tem tons suficientes para manter todas as séries distinguíveis
 * (o porquê, com os números, está em `utils/tokenComposition.ts`).
 */
export function TokenCompositionCard({ breakdown }: TokenCompositionCardProps) {
  const { t } = useTranslation('tutorAI');

  const { total, series: composition } = useMemo(
    () => buildTokenComposition(breakdown),
    [breakdown],
  );

  const series = useMemo(() => {
    const labelOf = (item: { provider: string; operation: string }) =>
      t('student.breakdown.seriesLabel', {
        provider: item.provider || t('student.breakdown.unknownProvider'),
        operation: item.operation || t('student.breakdown.unknownOperation'),
      });

    return composition.map(item => ({
      key: item.key,
      label: item.isOthers
        ? t('student.breakdown.others', { count: item.folded.length })
        : labelOf(item),
      // Nada se perde na dobra: as séries somadas ficam legíveis no title da entrada.
      detail: item.isOthers
        ? t('student.breakdown.othersDetail', {
            items: item.folded.map(labelOf).join(', '),
          })
        : undefined,
      colorClass: item.colorClass,
      tokensUsed: item.tokensUsed,
      percent: item.percent,
    }));
  }, [composition, t]);

  return (
    <SurfaceCard
      title={t('student.breakdown.title')}
      description={t('student.breakdown.description')}
    >
      <div className="flex flex-col gap-mini p-xxs">
        {total > 0 ? (
          <>
            <CompositionBar
              ariaLabel={series
                .map(item => `${item.label} ${formatPercent(item.percent)}`)
                .join(', ')}
              segments={series.map(item => ({
                key: item.key,
                colorClass: item.colorClass,
                percent: item.percent,
              }))}
            />

            <dl className="grid grid-cols-1 gap-mini sm:grid-cols-2 lg:grid-cols-3">
              {series.map(item => (
                <div key={item.key} className="flex flex-col gap-quark">
                  <dt
                    className="flex items-center gap-nano text-ctx-content-title ts-heading-xxs"
                    title={item.detail}
                  >
                    {/* Mesma altura da barra (`h-nano`): dobra a área de cor do fio de 4px
                        anterior, que é o que se compara ao ler a legenda. */}
                    <span className={`h-nano w-xxs shrink-0 rounded-pill ${item.colorClass}`} />
                    {item.label}
                  </dt>
                  <dd className="tabular-nums text-ctx-content-title ts-heading-xs">
                    {formatCount(item.tokensUsed)}
                  </dd>
                  <dd className="text-ctx-content-placeholder ts-caption-xxs">
                    {t('student.breakdown.share', { percent: formatPercent(item.percent) })}
                  </dd>
                </div>
              ))}
            </dl>
          </>
        ) : (
          <p className="py-xs text-center text-ctx-content-placeholder ts-paragraph-xs">
            {t('student.breakdown.empty')}
          </p>
        )}
      </div>
    </SurfaceCard>
  );
}
